import { Eye } from 'lucide-react';
import { AvatarGroup } from '@/components/ui/Avatar';
import { DiscussionSeenBy } from '@/lib/api';
import { formatRelativeTime } from '@/lib/utils';

/**
 * Read-receipt cluster for a discussion, agency-admin eyes only. The caller
 * gates rendering (the API only sends seenBy to admins, so presence alone is
 * already a gate — this component just renders what it's given).
 */
export function SeenBy({
  seenBy,
  currentUserId,
  size = 'xs',
}: {
  seenBy: DiscussionSeenBy[];
  currentUserId?: string;
  size?: 'xs' | 'sm';
}) {
  if (!seenBy || seenBy.length === 0) return null;

  const tooltip = seenBy
    .map((s) => `${s.name ?? 'Unknown'}${s.userId === currentUserId ? ' (you)' : ''} — ${formatRelativeTime(s.lastReadAt)}`)
    .join('\n');

  return (
    <div className="flex items-center gap-1.5 text-grey-400 no-print" title={tooltip}>
      <Eye className="w-3.5 h-3.5" />
      <span className="text-xs">Seen by {seenBy.length}</span>
      <AvatarGroup
        users={seenBy.map((s) => ({ name: s.name ?? 'Unknown', avatarUrl: s.avatarUrl ?? undefined }))}
        max={5}
        size={size}
      />
    </div>
  );
}

/**
 * Tiny per-reply variant: no avatars, just "Seen by N" with the names in the
 * tooltip. Derived client-side — a reply is seen by everyone whose discussion
 * lastReadAt is at or after the reply's createdAt.
 */
export function ReplySeenBy({
  seenBy,
  replyCreatedAt,
  currentUserId,
}: {
  seenBy: DiscussionSeenBy[];
  replyCreatedAt: string;
  currentUserId?: string;
}) {
  const replyTime = new Date(replyCreatedAt).getTime();
  const seen = (seenBy ?? []).filter((s) => new Date(s.lastReadAt).getTime() >= replyTime);
  if (seen.length === 0) return null;

  const tooltip = seen
    .map((s) => `${s.name ?? 'Unknown'}${s.userId === currentUserId ? ' (you)' : ''}`)
    .join('\n');

  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-grey-400 no-print" title={tooltip}>
      <Eye className="w-3 h-3" />
      Seen by {seen.length}
    </span>
  );
}
